import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useToast } from '../../Context/ToastContext';
import { useConfirm } from '../../Context/ConfirmContext';

const MondayCalendarTest = () => {
    const { showToast } = useToast();
    const { confirm } = useConfirm();
    const [boards, setBoards] = useState([]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [apiToken, setApiToken] = useState('');
    const [selectedBoardId, setSelectedBoardId] = useState('');
    const [editingItemId, setEditingItemId] = useState(null);
    const [editFormData, setEditFormData] = useState({});
    const [successMessage, setSuccessMessage] = useState('');
    const [showMeetingModal, setShowMeetingModal] = useState(false);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
    const [newMeeting, setNewMeeting] = useState({
        name: '',
        date: '',
        time: ''
    });
    const [boardsInfo, setBoardsInfo] = useState({ loaded: 0, hasMore: false });
    const [selectedBoards, setSelectedBoards] = useState([]);
    const [approvedBoards, setApprovedBoards] = useState([]);
    const [boardColumns, setBoardColumns] = useState({});
    const [boardItems, setBoardItems] = useState({});
    const [showBoardSelection, setShowBoardSelection] = useState(false);
    const [showCreateItemModal, setShowCreateItemModal] = useState(false);
    const [showBoardDataModal, setShowBoardDataModal] = useState(false);
    const [currentBoard, setCurrentBoard] = useState(null);
    const [newItemData, setNewItemData] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [searchColumn, setSearchColumn] = useState('all');

    // Monday.com API endpoint
    const MONDAY_API_URL = 'https://api.monday.com/v2';

    // Load token from localStorage on component mount
    useEffect(() => {
        const savedToken = localStorage.getItem('monday_api_token');
        if (savedToken) {
            setApiToken(savedToken);
        }
    }, []);

    // Save token to localStorage when it changes
    const handleTokenChange = (token) => {
        setApiToken(token);
        if (token) {
            localStorage.setItem('monday_api_token', token);
        } else {
            localStorage.removeItem('monday_api_token');
        }
    };

    // Clear stored token
    const clearToken = () => {
        setApiToken('');
        localStorage.removeItem('monday_api_token');
        setBoards([]);
        setItems([]);
        setSelectedBoardId('');
    };

    // Fetch boards from Monday.com
    const fetchBoards = async () => {
        if (!apiToken) {
            setError('Please enter your Monday.com API token');
            return;
        }

        setLoading(true);
        setError(null);

        const query = `
            query {
                boards (limit: 200) {
                    id
                    name
                    description
                    board_kind
                    board_folder_id
                    permissions
                }
            }
        `;

        try {
            const response = await axios.post(
                MONDAY_API_URL,
                { query },
                {
                    headers: {
                        'Authorization': apiToken,
                        'Content-Type': 'application/json',
                        'API-Version': '2024-10'
                    },
                }
            );

            if (response.data.errors) {
                setError(response.data.errors[0].message);
            } else {
                const fetchedBoards = response.data.data.boards;
                
                setBoards(fetchedBoards);
                setBoardsInfo({ 
                    loaded: fetchedBoards.length, 
                    hasMore: fetchedBoards.length === 200
                });
                
                // Show board selection modal instead of immediately displaying boards
                setShowBoardSelection(true);
                
                console.log('📊 Board Access Summary:');
                console.log(`✅ Boards loaded: ${fetchedBoards.length}`);
                console.log('📋 Board types:', 
                    fetchedBoards.reduce((acc, board) => {
                        acc[board.board_kind] = (acc[board.board_kind] || 0) + 1;
                        return acc;
                    }, {})
                );
                console.log('🔒 Permission samples:', 
                    fetchedBoards.slice(0, 3).map(board => ({
                        name: board.name,
                        kind: board.board_kind,
                        permissions: board.permissions
                    }))
                );
                
                if (fetchedBoards.length === 200) {
                    console.log('⚠️ WARNING: Reached 200 board limit - there may be more boards not shown!');
                    console.log('💡 This API call only fetches the first 200 boards');
                }
            }
        } catch (err) {
            setError(`Error: ${err.response?.data?.error_message || err.message}`);
            console.error('Error fetching boards:', err);
        } finally {
            setLoading(false);
        }
    };

    // Fetch items (calendar events) from a specific board
    const fetchBoardItems = async (boardId) => {
        if (!apiToken || !boardId) {
            return;
        }

        setLoading(true);
        setError(null);

        const query = `
            query {
                boards (ids: [${boardId}]) {
                    id
                    name
                    columns {
                        id
                        title
                        type
                    }
                    items_page(limit: 100) {
                        items {
                            id
                            name
                            created_at
                            updated_at
                            column_values {
                                id
                                text
                                value
                                type
                            }
                        }
                    }
                }
            }
        `;

        try {
            const response = await axios.post(
                MONDAY_API_URL,
                { query },
                {
                    headers: {
                        'Authorization': apiToken,
                        'Content-Type': 'application/json',
                        'API-Version': '2024-10'
                    },
                }
            );

            if (response.data.errors) {
                setError(response.data.errors[0].message);
            } else {
                const boardData = response.data.data.boards[0];
                const boardItems = boardData?.items_page?.items || [];
                const columns = boardData?.columns || [];
                setItems(boardItems);
                setBoardColumns(columns);
                console.log('Board data:', boardData);
                console.log('Items fetched:', boardItems);
                console.log('Columns:', columns);
            }
        } catch (err) {
            setError(`Error: ${err.response?.data?.error_message || err.message}`);
            console.error('Error fetching board items:', err);
        } finally {
            setLoading(false);
        }
    };

    // Handle board selection
    const handleBoardSelect = (boardId) => {
        setSelectedBoardId(boardId);
        fetchBoardItems(boardId);
    };

    // Format date columns
    const formatDateValue = (columnValue) => {
        if (columnValue.type === 'date' && columnValue.value) {
            try {
                const dateData = JSON.parse(columnValue.value);
                if (dateData.date && dateData.time) {
                    return `${dateData.date} ${dateData.time.substring(0, 5)}`;
                }
                return dateData.date || columnValue.text;
            } catch {
                return columnValue.text;
            }
        }
        return columnValue.text;
    };

    // Time slots from 8 AM to 6 PM
    const timeSlots = [
        '08:00', '09:00', '10:00', '11:00', '12:00', 
        '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'
    ];

    // Get meetings for selected date
    const getMeetingsForDate = () => {
        return items.filter(item => {
            const dateColumn = item.column_values.find(col => col.type === 'date' && col.value);
            if (dateColumn && dateColumn.value) {
                try {
                    const dateData = JSON.parse(dateColumn.value);
                    return dateData.date === selectedDate;
                } catch {
                    return false;
                }
            }
            return false;
        });
    };

    // Check if time slot is occupied
    const isTimeSlotOccupied = (timeSlot) => {
        const meetings = getMeetingsForDate();
        return meetings.some(meeting => {
            const dateColumn = meeting.column_values.find(col => col.type === 'date' && col.value);
            if (dateColumn && dateColumn.value) {
                try {
                    const dateData = JSON.parse(dateColumn.value);
                    return dateData.time && dateData.time.substring(0, 5) === timeSlot;
                } catch {
                    return false;
                }
            }
            return false;
        });
    };

    // Create new meeting
    const createMeeting = async () => {
        if (!newMeeting.name || !newMeeting.date || !newMeeting.time) {
            setError('Please fill in all meeting details');
            return;
        }

        setLoading(true);
        setError(null);

        const dateColumn = boardColumns.find(col => col.type === 'date');
        
        if (!dateColumn) {
            setError('No date column found in this board');
            setLoading(false);
            return;
        }

        const dateValueString = JSON.stringify({
            date: newMeeting.date,
            time: newMeeting.time + ':00'
        });

        const createMutation = `
            mutation {
                create_item (
                    board_id: ${selectedBoardId},
                    item_name: ${JSON.stringify(newMeeting.name)},
                    column_values: ${JSON.stringify(JSON.stringify({ [dateColumn.id]: { date: newMeeting.date, time: newMeeting.time + ':00' } }))}
                ) {
                    id
                    name
                }
            }
        `;

        try {
            const response = await axios.post(
                MONDAY_API_URL,
                { query: createMutation },
                {
                    headers: {
                        'Authorization': apiToken,
                        'Content-Type': 'application/json',
                        'API-Version': '2024-10'
                    },
                }
            );

            if (response.data.errors) {
                setError(response.data.errors[0].message);
                console.error('Create meeting error:', response.data.errors);
            } else {
                setSuccessMessage('✓ Meeting created successfully!');
                setShowMeetingModal(false);
                setNewMeeting({ name: '', date: '', time: '' });
                setSelectedTimeSlot(null);
                await fetchBoardItems(selectedBoardId);
                setTimeout(() => setSuccessMessage(''), 3000);
            }
        } catch (err) {
            setError(`Error creating meeting: ${err.response?.data?.error_message || err.message}`);
            console.error('Error creating meeting:', err);
        } finally {
            setLoading(false);
        }
    };

    // Toggle board selection
    const toggleBoardSelection = (boardId) => {
        setSelectedBoards(prev => 
            prev.includes(boardId) 
                ? prev.filter(id => id !== boardId)
                : [...prev, boardId]
        );
    };

    // Fetch dropdown/status options for columns that need them
    const fetchColumnOptions = async (boardColumnsData) => {
        console.log('🔍 Starting to fetch column options...');
        
        for (const [boardId, columns] of Object.entries(boardColumnsData)) {
            console.log(`🔍 Processing board ${boardId} with ${columns.length} columns`);
            
            for (const column of columns) {
                if (column.type === 'status' || column.type === 'dropdown') {
                    console.log(`🔍 Fetching options for ${column.title} (${column.type})`);
                    
                    try {
                        // Use a simpler query to get column details
                        const optionsQuery = `
                            query {
                                boards (ids: [${boardId}]) {
                                    columns (ids: ["${column.id}"]) {
                                        id
                                        title
                                        type
                                        settings_str
                                    }
                                }
                            }
                        `;

                        const response = await axios.post(
                            MONDAY_API_URL,
                            { query: optionsQuery },
                            {
                                headers: {
                                    'Authorization': apiToken,
                                    'Content-Type': 'application/json',
                                    'API-Version': '2024-10'
                                },
                            }
                        );

                        console.log(`📊 Response for ${column.title}:`, response.data);

                        if (response.data.errors) {
                            console.error(`❌ Error fetching options for ${column.title}:`, response.data.errors);
                        } else if (response.data.data.boards[0]?.columns[0]) {
                            const columnData = response.data.data.boards[0].columns[0];
                            console.log(`📋 Column data:`, columnData);
                            
                            if (columnData.settings_str) {
                                try {
                                    const settings = JSON.parse(columnData.settings_str);
                                    console.log(`⚙️ Settings parsed:`, settings);
                                    
                                    if (settings.labels && Array.isArray(settings.labels)) {
                                        column.labels = settings.labels;
                                        console.log(`✅ Loaded ${column.labels.length} options for ${column.title}:`, column.labels.map(l => l.name));
                                    } else {
                                        console.log(`⚠️ No labels found in settings for ${column.title}`);
                                        column.labels = [];
                                    }
                                } catch (e) {
                                    console.error(`❌ Could not parse settings for ${column.title}:`, e);
                                    column.labels = [];
                                }
                            } else {
                                console.log(`⚠️ No settings_str for ${column.title}`);
                                column.labels = [];
                            }
                        } else {
                            console.log(`⚠️ No column data returned for ${column.title}`);
                            column.labels = [];
                        }
                    } catch (e) {
                        console.error(`❌ Error fetching options for ${column.title}:`, e);
                        column.labels = [];
                    }
                } else {
                    // Ensure other column types have an empty labels array
                    column.labels = [];
                }
            }
        }
        
        console.log('🎯 Final board columns data:', boardColumnsData);
    };

    // Approve board selection and fetch their columns AND items
    const approveBoardSelection = async () => {
        if (selectedBoards.length === 0) {
            setError('Please select at least one board');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Fetch columns AND items for all selected boards
            const boardColumnsData = {};
            const boardItemsData = {};
            
            for (const boardId of selectedBoards) {
                console.log(`🔍 Fetching ALL data for board ${boardId} with pagination...`);
                
                // First, get board structure and first batch of items
                const query = `
                    query {
                        boards (ids: [${boardId}]) {
                            id
                            name
                            board_kind
                            description
                            columns {
                                id
                                title
                                type
                                settings_str
                            }
                            items_page(limit: 500) {
                                cursor
                                items {
                                    id
                                    name
                                    created_at
                                    updated_at
                                    column_values {
                                        id
                                        text
                                        value
                                        type
                                    }
                                }
                            }
                        }
                    }
                `;

                const response = await axios.post(
                    MONDAY_API_URL,
                    { query },
                    {
                        headers: {
                            'Authorization': apiToken,
                            'Content-Type': 'application/json',
                            'API-Version': '2024-10'
                        },
                    }
                );

                if (response.data.errors) {
                    console.error(`❌ Error fetching data for board ${boardId}:`, response.data.errors);
                    console.error('Specific error messages:');
                    response.data.errors.forEach((error, idx) => {
                        console.error(`  ${idx + 1}. ${error.message}`);
                        console.error(`     Path: ${error.path ? error.path.join(' -> ') : 'N/A'}`);
                    });
                    console.error('Full response:', response.data);
                } else {
                    const board = response.data.data.boards[0];
                    console.log(`📋 Board "${board?.name}" - First batch loaded`);
                    
                    if (board) {
                        boardColumnsData[boardId] = board.columns || [];
                        let allItems = board.items_page?.items || [];
                        let cursor = board.items_page?.cursor;
                        
                        console.log(`📦 First batch: ${allItems.length} items, cursor: ${cursor}`);

                        // Fetch remaining items if there are more (pagination)
                        let pageCount = 1;
                        while (cursor && pageCount < 5) { // Limit to 5 pages (2500 items max) to avoid timeout
                            pageCount++;
                            console.log(`📄 Fetching page ${pageCount} with cursor...`);
                            
                            const paginationQuery = `
                                query {
                                    boards (ids: [${boardId}]) {
                                        items_page(limit: 500, cursor: "${cursor}") {
                                            cursor
                                            items {
                                                id
                                                name
                                                created_at
                                                updated_at
                                                column_values {
                                                    id
                                                    text
                                                    value
                                                    type
                                                }
                                            }
                                        }
                                    }
                                }
                            `;

                            const pageResponse = await axios.post(
                                MONDAY_API_URL,
                                { query: paginationQuery },
                                {
                                    headers: {
                                        'Authorization': apiToken,
                                        'Content-Type': 'application/json',
                                        'API-Version': '2024-10'
                                    },
                                }
                            );

                            if (!pageResponse.data.errors) {
                                const pageData = pageResponse.data.data.boards[0]?.items_page;
                                if (pageData?.items) {
                                    allItems = [...allItems, ...pageData.items];
                                    cursor = pageData.cursor;
                                    console.log(`✅ Page ${pageCount}: Added ${pageData.items.length} items. Total: ${allItems.length}`);
                                } else {
                                    break;
                                }
                            } else {
                                console.error(`❌ Error fetching page ${pageCount}:`, pageResponse.data.errors);
                                break;
                            }
                        }

                        boardItemsData[boardId] = allItems;
                        
                        console.log(`✅ Board "${board.name}" fully loaded:`, {
                            boardId: board.id,
                            columns: board.columns?.length || 0,
                            items: allItems.length,
                            pages: pageCount,
                            columnTypes: (board.columns || []).reduce((acc, col) => {
                                acc[col.type] = (acc[col.type] || 0) + 1;
                                return acc;
                            }, {})
                        });
                    } else {
                        console.error(`❌ No board data returned for ${boardId}`);
                    }
                }
            }

            console.log('🔧 Setting state with data:', {
                boardColumnsData,
                boardItemsData,
                totalBoardsProcessed: Object.keys(boardColumnsData).length
            });

            // Extract status/dropdown options from existing item values
            console.log('🔍 Starting option extraction from existing data...');
            
            for (const [boardId, items] of Object.entries(boardItemsData)) {
                const columns = boardColumnsData[boardId] || [];
                console.log(`📋 Processing ${items.length} items for board ${boardId}`);
                
                columns.forEach(column => {
                    console.log(`🔍 Checking column: ${column.title} (${column.type})`);
                    
                    if (column.type === 'status' || column.type === 'dropdown') {
                        // Extract unique values from existing items
                        const uniqueValues = new Set();
                        
                        items.forEach(item => {
                            const columnValue = item.column_values.find(cv => cv.id === column.id);
                            console.log(`📊 Item "${item.name}" - Column "${column.title}":`, columnValue);
                            
                            if (columnValue && columnValue.text && columnValue.text.trim()) {
                                uniqueValues.add(columnValue.text);
                                console.log(`✅ Found value: "${columnValue.text}"`);
                            }
                        });
                        
                        console.log(`🎯 Unique values found for ${column.title}:`, Array.from(uniqueValues));
                        
                        // Convert to label format
                        if (uniqueValues.size > 0) {
                            column.labels = Array.from(uniqueValues).map((value, index) => ({
                                id: index,
                                name: value
                            }));
                            console.log(`✅ Created ${column.labels.length} options for ${column.title}:`, column.labels);
                        } else {
                            console.log(`⚠️ No existing values found for ${column.title}`);
                            
                            // Add some default options if we can't extract any
                            if (column.title.toLowerCase().includes('lead')) {
                                column.labels = [
                                    { id: 1, name: 'Rita' },
                                    { id: 2, name: 'Phone' },
                                    { id: 3, name: 'Referral' },
                                    { id: 4, name: 'Whatsapp' }
                                ];
                                console.log(`📝 Added default options for ${column.title}`);
                            } else if (column.title.toLowerCase().includes('status')) {
                                column.labels = [
                                    { id: 1, name: 'New' },
                                    { id: 2, name: 'In Progress' },
                                    { id: 3, name: 'Completed' },
                                    { id: 4, name: 'On Hold' }
                                ];
                                console.log(`📝 Added default options for ${column.title}`);
                            } else {
                                column.labels = [];
                            }
                        }
                    } else {
                        column.labels = [];
                    }
                });
            }

            // Also try fetching from API (as backup)
            try {
                await fetchColumnOptions(boardColumnsData);
                console.log('✅ Column options loaded from API');
            } catch (e) {
                console.log('⚠️ Some column options failed to load from API:', e);
            }

            setBoardColumns(boardColumnsData);
            setBoardItems(boardItemsData);
            setApprovedBoards(selectedBoards);
            setShowBoardSelection(false);
            setSuccessMessage(`✓ ${selectedBoards.length} boards loaded with full data!`);
            setTimeout(() => setSuccessMessage(''), 3000);

        } catch (err) {
            setError(`Error fetching board data: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Create new item in selected board
    const createBoardItem = async () => {
        if (!currentBoard || !newItemData.name) {
            setError('Please fill in the item name');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Build column values object
            const columnValues = {};
            const columns = boardColumns[currentBoard.id] || [];

            // Add all filled fields with proper formatting
            Object.keys(newItemData).forEach(columnId => {
                if (columnId === 'name') return; // Skip name, it's handled separately
                
                const value = newItemData[columnId];
                if (value && value.toString().trim()) {
                    const column = columns.find(col => col.id === columnId);
                    if (column) {
                        // Format values based on column type
                        switch (column.type) {
                            case 'date':
                                columnValues[columnId] = { date: value };
                                break;
                            case 'status':
                                columnValues[columnId] = { label: value };
                                break;
                            case 'dropdown':
                                columnValues[columnId] = { ids: [value] }; // Assuming single selection
                                break;
                            case 'numbers':
                                columnValues[columnId] = parseFloat(value) || 0;
                                break;
                            case 'email':
                            case 'phone':
                            case 'url':
                            case 'text':
                            case 'long-text':
                            default:
                                columnValues[columnId] = value;
                                break;
                        }
                    }
                }
            });

            console.log('Creating item with values:', {
                name: newItemData.name,
                columnValues: columnValues,
                boardType: currentBoard.board_kind
            });

            const mutation = `
                mutation {
                    create_item (
                        board_id: ${currentBoard.id},
                        item_name: ${JSON.stringify(newItemData.name)},
                        column_values: ${JSON.stringify(JSON.stringify(columnValues))}
                    ) {
                        id
                        name
                    }
                }
            `;

            const response = await axios.post(
                MONDAY_API_URL,
                { query: mutation },
                {
                    headers: {
                        'Authorization': apiToken,
                        'Content-Type': 'application/json',
                        'API-Version': '2024-10'
                    },
                }
            );

            if (response.data.errors) {
                setError(response.data.errors[0].message);
                console.error('Create item error:', response.data.errors);
            } else {
                setSuccessMessage(`✓ New ${currentBoard.board_kind} item "${newItemData.name}" created successfully!`);
                setShowCreateItemModal(false);
                setNewItemData({});
                setCurrentBoard(null);
                setTimeout(() => setSuccessMessage(''), 3000);
            }
        } catch (err) {
            setError(`Error creating item: ${err.response?.data?.error_message || err.message}`);
            console.error('Error creating item:', err);
        } finally {
            setLoading(false);
        }
    };

    // Open create item modal for specific board
    const openCreateItemModal = (board) => {
        setCurrentBoard(board);
        setNewItemData({ name: '' });
        setShowCreateItemModal(true);
    };

    // Open board data view modal
    const openBoardDataModal = (board) => {
        setCurrentBoard(board);
        setShowBoardDataModal(true);
        setSearchTerm(''); // Reset search when opening modal
        setSearchColumn('all');
    };

    // Filter items based on search
    const getFilteredItems = () => {
        if (!currentBoard) return [];
        
        const items = boardItems[currentBoard.id] || [];
        
        if (!searchTerm.trim()) {
            return items; // No search, return all
        }

        const searchLower = searchTerm.toLowerCase();

        return items.filter(item => {
            // Search in item name
            if (searchColumn === 'all' || searchColumn === 'name') {
                if (item.name.toLowerCase().includes(searchLower)) {
                    return true;
                }
            }

            // Search in specific column or all columns
            if (searchColumn === 'all') {
                // Search across all column values
                return item.column_values.some(col => {
                    const text = col.text || '';
                    return text.toLowerCase().includes(searchLower);
                });
            } else {
                // Search in specific column
                const columnValue = item.column_values.find(cv => cv.id === searchColumn);
                if (columnValue && columnValue.text) {
                    return columnValue.text.toLowerCase().includes(searchLower);
                }
            }

            return false;
        });
    };

    // Handle time slot selection
    const handleTimeSlotClick = (timeSlot) => {
        const meetings = getMeetingsForDate();
        const existingMeeting = meetings.find(meeting => {
            const dateColumn = meeting.column_values.find(col => col.type === 'date' && col.value);
            if (dateColumn && dateColumn.value) {
                try {
                    const dateData = JSON.parse(dateColumn.value);
                    return dateData.time && dateData.time.substring(0, 5) === timeSlot;
                } catch {
                    return false;
                }
            }
            return false;
        });

        if (existingMeeting) {
            // If slot is occupied, select it to show meeting details
            setSelectedTimeSlot(timeSlot);
            setNewMeeting({
                name: existingMeeting.name,
                date: selectedDate,
                time: timeSlot,
                id: existingMeeting.id // Store the meeting ID for potential deletion
            });
        } else {
            // If slot is free, select it for new meeting
            setSelectedTimeSlot(timeSlot);
            setNewMeeting({
                name: '',
                date: selectedDate,
                time: timeSlot
            });
        }
    };

    // Delete meeting/item
    const deleteItem = async (itemId, itemName) => {
        const confirmed = await confirm({
            title: 'Delete Meeting',
            message: `Are you sure you want to delete "${itemName}"?`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            type: 'danger'
        });
        
        if (!confirmed) return;

        setLoading(true);
        setError(null);

        const deleteMutation = `
            mutation {
                delete_item (
                    item_id: ${itemId}
                ) {
                    id
                }
            }
        `;

        try {
            const response = await axios.post(
                MONDAY_API_URL,
                { query: deleteMutation },
                {
                    headers: {
                        'Authorization': apiToken,
                        'Content-Type': 'application/json',
                        'API-Version': '2024-10'
                    },
                }
            );

            if (response.data.errors) {
                setError(response.data.errors[0].message);
                console.error('Delete item error:', response.data.errors);
            } else {
                setSuccessMessage(`✓ "${itemName}" deleted successfully!`);
                await fetchBoardItems(selectedBoardId);
                
                // If deleting from modal, close it
                if (showMeetingModal) {
                    setShowMeetingModal(false);
                    setSelectedTimeSlot(null);
                    setNewMeeting({ name: '', date: '', time: '' });
                }
                
                setTimeout(() => setSuccessMessage(''), 3000);
            }
        } catch (err) {
            setError(`Error deleting item: ${err.response?.data?.error_message || err.message}`);
            console.error('Error deleting item:', err);
        } finally {
            setLoading(false);
        }
    };

    // Start editing an item
    const startEditing = (item) => {
        setEditingItemId(item.id);
        const formData = {
            name: item.name,
            columns: {},
            times: {}  // Store times separately
        };
        
        // Populate column values
        item.column_values.forEach(col => {
            if (col.type === 'date' && col.value) {
                try {
                    const dateData = JSON.parse(col.value);
                    formData.columns[col.id] = dateData.date || '';
                    formData.times[col.id] = dateData.time ? dateData.time.substring(0, 5) : '09:00';
                } catch {
                    formData.columns[col.id] = col.text || '';
                    formData.times[col.id] = '09:00';
                }
            } else {
                formData.columns[col.id] = col.text || '';
            }
        });
        
        setEditFormData(formData);
        setError(null);
        setSuccessMessage('');
    };

    // Cancel editing
    const cancelEditing = () => {
        setEditingItemId(null);
        setEditFormData({});
        setError(null);
    };

    // Update item name
    const updateItemName = async (itemId, newName) => {
        if (!apiToken || !newName.trim()) {
            return;
        }

        setLoading(true);
        setError(null);

        const mutation = `
            mutation {
                change_simple_column_value (
                    item_id: ${itemId},
                    board_id: ${selectedBoardId},
                    column_id: "name",
                    value: "${newName.replace(/"/g, '\\"')}"
                ) {
                    id
                    name
                }
            }
        `;

        try {
            const response = await axios.post(
                MONDAY_API_URL,
                { query: mutation },
                {
                    headers: {
                        'Authorization': apiToken,
                        'Content-Type': 'application/json',
                        'API-Version': '2024-10'
                    },
                }
            );

            if (response.data.errors) {
                setError(response.data.errors[0].message);
            } else {
                console.log('Item name updated:', response.data);
            }
        } catch (err) {
            setError(`Error updating name: ${err.response?.data?.error_message || err.message}`);
            console.error('Error updating item name:', err);
        } finally {
            setLoading(false);
        }
    };

    // Update column value
    const updateColumnValue = async (itemId, columnId, value) => {
        if (!apiToken) {
            return;
        }

        setLoading(true);
        setError(null);

        // Find column type
        const column = boardColumns.find(col => col.id === columnId);
        const columnType = column?.type || 'text';

        let formattedValue = value;
        
        // Format value based on column type
        if (columnType === 'date') {
            formattedValue = JSON.stringify({ date: value });
        } else if (columnType === 'text' || columnType === 'long-text') {
            formattedValue = value;
        }

        const mutation = `
            mutation {
                change_simple_column_value (
                    item_id: ${itemId},
                    board_id: ${selectedBoardId},
                    column_id: "${columnId}",
                    value: ${JSON.stringify(formattedValue)}
                ) {
                    id
                }
            }
        `;

        try {
            const response = await axios.post(
                MONDAY_API_URL,
                { query: mutation },
                {
                    headers: {
                        'Authorization': apiToken,
                        'Content-Type': 'application/json',
                        'API-Version': '2024-10'
                    },
                }
            );

            if (response.data.errors) {
                setError(response.data.errors[0].message);
                return false;
            } else {
                console.log('Column updated:', response.data);
                return true;
            }
        } catch (err) {
            setError(`Error updating column: ${err.response?.data?.error_message || err.message}`);
            console.error('Error updating column:', err);
            return false;
        } finally {
            setLoading(false);
        }
    };

    // Save all changes
    const saveChanges = async () => {
        if (!editingItemId || !editFormData.name) {
            return;
        }

        setLoading(true);
        setError(null);
        setSuccessMessage('');

        try {
            // First update the item name
            const nameUpdateMutation = `
                mutation {
                    change_simple_column_value (
                        item_id: ${editingItemId},
                        board_id: ${selectedBoardId},
                        column_id: "name",
                        value: ${JSON.stringify(editFormData.name)}
                    ) {
                        id
                    }
                }
            `;

            console.log('Name mutation:', nameUpdateMutation);

            const nameResponse = await axios.post(
                MONDAY_API_URL,
                { query: nameUpdateMutation },
                {
                    headers: {
                        'Authorization': apiToken,
                        'Content-Type': 'application/json',
                        'API-Version': '2024-10'
                    },
                }
            );

            if (nameResponse.data.errors) {
                console.error('Name update errors:', nameResponse.data.errors);
                setError(`Error updating name: ${nameResponse.data.errors[0].message}`);
                return;
            }

            // Update each column value individually to avoid conflicts
            for (const columnId of Object.keys(editFormData.columns)) {
                const column = boardColumns.find(col => col.id === columnId);
                const value = editFormData.columns[columnId];
                
                // Only update editable column types
                if (column && value && value.toString().trim() && 
                    (column.type === 'text' || column.type === 'date' || column.type === 'long-text')) {
                    
                    let mutation;
                    
                    if (column.type === 'date') {
                        // For date columns, Monday.com expects a JSON STRING (even with JSON! type)
                        const timeValue = editFormData.times?.[columnId] || '09:00';
                        const dateValueString = JSON.stringify({
                            date: value,
                            time: timeValue + ':00'
                        });
                        
                        mutation = {
                            query: `
                                mutation {
                                    change_column_value(
                                        board_id: ${selectedBoardId},
                                        item_id: ${editingItemId},
                                        column_id: "${columnId}",
                                        value: ${JSON.stringify(dateValueString)}
                                    ) {
                                        id
                                    }
                                }
                            `
                        };
                    } else {
                        // For text columns, use change_simple_column_value with String type
                        mutation = {
                            query: `
                                mutation($boardId: ID!, $itemId: ID!, $columnId: String!, $value: String!) {
                                    change_simple_column_value(
                                        board_id: $boardId,
                                        item_id: $itemId,
                                        column_id: $columnId,
                                        value: $value
                                    ) {
                                        id
                                    }
                                }
                            `,
                            variables: {
                                boardId: selectedBoardId.toString(),
                                itemId: editingItemId.toString(),
                                columnId: columnId,
                                value: value
                            }
                        };
                    }

                    console.log(`Updating column ${columnId} (${column.type})`);
                    console.log('Mutation:', mutation.query || mutation);
                    if (mutation.variables) {
                        console.log('Variables:', mutation.variables);
                    }

                    const requestBody = mutation.query 
                        ? mutation  // Has variables
                        : { query: mutation };  // Just a query string
                    
                    const response = await axios.post(
                        MONDAY_API_URL,
                        requestBody,
                        {
                            headers: {
                                'Authorization': apiToken,
                                'Content-Type': 'application/json',
                                'API-Version': '2024-10'
                            },
                        }
                    );

                    if (response.data.errors) {
                        const errorMsg = response.data.errors[0]?.message || 'Unknown error';
                        console.error(`❌ ERROR: ${errorMsg}`);
                        console.error('Full error object:', response.data.errors[0]);
                        console.error('Mutation sent:', mutation.query || mutation);
                        
                        setError(`Error updating ${column.title || columnId}: ${errorMsg}`);
                        return;
                    } else {
                        console.log(`✓ Column ${columnId} updated successfully`);
                        console.log('Response:', response.data);
                    }
                }
            }

            setSuccessMessage('✓ Item updated successfully!');
            setEditingItemId(null);
            setEditFormData({});
            
            // Refresh the board items
            await fetchBoardItems(selectedBoardId);
            
            // Clear success message after 3 seconds
            setTimeout(() => setSuccessMessage(''), 3000);

        } catch (err) {
            const errorMsg = err.response?.data?.error_message 
                || err.response?.data?.errors?.[0]?.message 
                || err.message;
            setError(`Error saving changes: ${errorMsg}`);
            console.error('Error saving changes:', err);
            console.error('Error response:', err.response?.data);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ 
            padding: '20px', 
            maxWidth: '1400px', 
            margin: '0 auto',
            height: '100vh',
            overflowY: 'auto',
            overflowX: 'hidden'
        }}>
            <h1 style={{ color: '#333', marginBottom: '10px' }}>Monday.com Calendar Test Page</h1>
            <p style={{ color: '#666', marginBottom: '20px' }}>
                Test your Monday.com integration and view calendar data
            </p>

            {/* API Token Input */}
            <div style={{ marginBottom: '20px', padding: '20px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                <h3 style={{ marginTop: 0 }}>Step 1: Enter your Monday.com API Token</h3>
                <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
                    Get your token from: Monday.com → Profile → Developers → My Access Tokens
                </p>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                    <input
                        type="password"
                        value={apiToken}
                        onChange={(e) => handleTokenChange(e.target.value)}
                        placeholder="Enter your Monday.com API token"
                        style={{
                            flex: 1,
                            padding: '12px',
                            fontSize: '14px',
                            borderRadius: '4px',
                            border: '1px solid #ddd',
                        }}
                    />
                    <button
                        onClick={fetchBoards}
                        disabled={loading || !apiToken}
                        style={{
                            padding: '12px 24px',
                            backgroundColor: apiToken ? '#0073ea' : '#ccc',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: apiToken ? 'pointer' : 'not-allowed',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            transition: 'background-color 0.2s'
                        }}
                    >
                        {loading ? 'Loading...' : 'Fetch Boards'}
                    </button>
                    {apiToken && (
                        <button
                            onClick={clearToken}
                            style={{
                                padding: '12px 24px',
                                backgroundColor: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '14px'
                            }}
                        >
                            Clear Token
                        </button>
                    )}
                </div>
                {apiToken && (
                    <p style={{ fontSize: '12px', color: '#28a745', marginTop: '10px' }}>
                        ✓ Token saved in browser storage
                    </p>
                )}
            </div>

            {/* Success Message */}
            {successMessage && (
                <div style={{
                    padding: '15px',
                    backgroundColor: '#d4edda',
                    color: '#155724',
                    borderRadius: '4px',
                    marginBottom: '20px',
                    border: '1px solid #c3e6cb',
                    fontWeight: 'bold'
                }}>
                    {successMessage}
                </div>
            )}

            {/* Error Display */}
            {error && (
                <div style={{
                    padding: '15px',
                    backgroundColor: '#fee',
                    color: '#c00',
                    borderRadius: '4px',
                    marginBottom: '20px',
                    border: '1px solid #fcc'
                }}>
                    <strong>Error:</strong> {error}
                </div>
            )}

            {/* Approved Boards - Only show these after selection */}
            {approvedBoards.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                    <h3>Step 2: Your Selected Boards ({approvedBoards.length} boards)</h3>
                    <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
                        gap: '20px',
                        marginTop: '15px'
                    }}>
                        {approvedBoards.map((boardId) => {
                            const board = boards.find(b => b.id === boardId);
                            const columns = boardColumns[boardId] || [];
                            const items = boardItems[boardId] || [];
                            
                            console.log(`🖥️ Rendering board ${boardId}:`, {
                                board: board?.name,
                                columnsCount: columns.length,
                                itemsCount: items.length,
                                boardColumnsState: !!boardColumns[boardId],
                                boardItemsState: !!boardItems[boardId]
                            });
                            
                            return (
                                <div
                                    key={boardId}
                                    style={{
                                        padding: '20px',
                                        border: '2px solid #0073ea',
                                        borderRadius: '12px',
                                        backgroundColor: 'white',
                                        boxShadow: '0 4px 12px rgba(0,115,234,0.1)'
                                    }}
                                >
                                    <div style={{ marginBottom: '15px' }}>
                                        <h4 style={{ margin: '0 0 8px 0', color: '#0073ea', fontSize: '18px' }}>
                                            {board?.name}  
                                        </h4>
                                        <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#666' }}>
                                            Type: <strong>{board?.board_kind}</strong> | {columns.length} columns | {items.length} items
                                        </p>
                                        {board?.description && (
                                            <p style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#666' }}>
                                                {board.description}
                                            </p>
                                        )}
                                    </div>

                                    <div style={{ marginBottom: '15px' }}>
                                        <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#888', fontWeight: 'bold' }}>
                                            Available Fields:
                                        </p>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                            {columns.length > 0 ? (
                                                <>
                                                    {columns.slice(0, 6).map(col => (
                                                        <span 
                                                            key={col.id} 
                                                            style={{ 
                                                                padding: '2px 8px', 
                                                                backgroundColor: '#f0f8ff', 
                                                                border: '1px solid #0073ea20', 
                                                                borderRadius: '12px', 
                                                                fontSize: '11px',
                                                                color: '#0073ea'
                                                            }}
                                                        >
                                                            {col.title} ({col.type})
                                                        </span>
                                                    ))}
                                                    {columns.length > 6 && (
                                                        <span style={{ fontSize: '11px', color: '#666' }}>
                                                            +{columns.length - 6} more
                                                        </span>
                                                    )}
                                                </>
                                            ) : (
                                                <span style={{ fontSize: '11px', color: '#999', fontStyle: 'italic' }}>
                                                    Loading board structure...
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <button
                                            onClick={() => openBoardDataModal(board)}
                                            style={{
                                                flex: 1,
                                                padding: '12px',
                                                backgroundColor: '#0073ea',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                fontWeight: 'bold',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '8px'
                                            }}
                                        >
                                            <span>👁️</span>
                                            View Data
                                        </button>
                                        <button
                                            onClick={() => openCreateItemModal(board)}
                                            style={{
                                                flex: 1,
                                                padding: '12px',
                                                backgroundColor: '#28a745',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                fontWeight: 'bold',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '8px'
                                            }}
                                        >
                                            <span>➕</span>
                                            Add New
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    
                    <div style={{ 
                        marginTop: '20px', 
                        padding: '15px', 
                        backgroundColor: '#e7f3ff', 
                        borderRadius: '8px',
                        border: '1px solid #b3d9ff',
                        fontSize: '14px',
                        color: '#004085'
                    }}>
                        <strong>💡 How it works:</strong> Each board has its own set of fields (columns). 
                        Click "Add New..." to create items with all the specific fields for that board type.
                        For example: Calendar boards → meetings, CRM boards → leads/customers, Project boards → tasks.
                    </div>
                </div>
            )}

            {/* Create Meeting Button */}
            {selectedBoardId && !editingItemId && (
                <div style={{ marginBottom: '20px' }}>
                    <button
                        onClick={() => setShowMeetingModal(true)}
                        style={{
                            padding: '12px 24px',
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        <span>📅</span> Create Meeting
                    </button>
                </div>
            )}

            {/* Board Items/Calendar Events */}
            {items.length > 0 && (
                <div>
                    <h3>Step 3: Board Items ({items.length} items found)</h3>
                    <div style={{ 
                        overflowX: 'auto', 
                        marginTop: '15px',
                        maxHeight: '600px',
                        overflowY: 'auto',
                        position: 'relative'
                    }}>
                        <table style={{ 
                            width: '100%', 
                            borderCollapse: 'collapse',
                            backgroundColor: 'white',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            borderRadius: '8px',
                            overflow: 'hidden'
                        }}>
                            <thead>
                                <tr style={{ backgroundColor: '#0073ea', color: 'white' }}>
                                    <th style={{ padding: '14px', textAlign: 'left', fontWeight: 'bold' }}>Item Name</th>
                                    <th style={{ padding: '14px', textAlign: 'left', fontWeight: 'bold' }}>Created At</th>
                                    <th style={{ padding: '14px', textAlign: 'left', fontWeight: 'bold' }}>Updated At</th>
                                    <th style={{ padding: '14px', textAlign: 'left', fontWeight: 'bold' }}>Column Details</th>
                                    <th style={{ padding: '14px', textAlign: 'left', fontWeight: 'bold' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item, idx) => (
                                    <tr 
                                        key={item.id} 
                                        style={{ 
                                            borderBottom: '1px solid #eee',
                                            backgroundColor: editingItemId === item.id ? '#fff8e1' : (idx % 2 === 0 ? 'white' : '#f9f9f9')
                                        }}
                                    >
                                        <td style={{ padding: '14px', fontWeight: '500' }}>
                                            {editingItemId === item.id ? (
                                                <input
                                                    type="text"
                                                    value={editFormData.name || ''}
                                                    onChange={(e) => setEditFormData({
                                                        ...editFormData,
                                                        name: e.target.value
                                                    })}
                                                    style={{
                                                        width: '100%',
                                                        padding: '8px',
                                                        border: '1px solid #0073ea',
                                                        borderRadius: '4px',
                                                        fontSize: '14px'
                                                    }}
                                                />
                                            ) : (
                                                item.name
                                            )}
                                        </td>
                                        <td style={{ padding: '14px', fontSize: '13px', color: '#666' }}>
                                            {new Date(item.created_at).toLocaleString()}
                                        </td>
                                        <td style={{ padding: '14px', fontSize: '13px', color: '#666' }}>
                                            {new Date(item.updated_at).toLocaleString()}
                                        </td>
                                        <td style={{ padding: '14px' }}>
                                            {editingItemId === item.id ? (
                                                <div style={{ 
                                                    padding: '12px', 
                                                    backgroundColor: '#f0f4f8', 
                                                    borderRadius: '4px',
                                                    border: '1px solid #d0dae5'
                                                }}>
                                                    {item.column_values
                                                        .filter(col => col.type === 'text' || col.type === 'date' || col.type === 'long-text')
                                                        .map((col, idx) => {
                                                            const columnInfo = boardColumns.find(c => c.id === col.id);
                                                            return (
                                                                <div key={idx} style={{ marginBottom: '10px' }}>
                                                                    <label style={{ 
                                                                        display: 'block', 
                                                                        fontSize: '12px', 
                                                                        color: '#666',
                                                                        marginBottom: '4px',
                                                                        fontWeight: '500'
                                                                    }}>
                                                                        {columnInfo?.title || col.id}
                                                                        <span style={{ color: '#999', fontSize: '11px', marginLeft: '6px' }}>
                                                                            ({col.type})
                                                                        </span>
                                                                    </label>
                                                                    {col.type === 'date' ? (
                                                                        <div style={{ display: 'flex', gap: '8px' }}>
                                                                            <input
                                                                                type="date"
                                                                                value={editFormData.columns?.[col.id] || ''}
                                                                                onChange={(e) => setEditFormData({
                                                                                    ...editFormData,
                                                                                    columns: {
                                                                                        ...editFormData.columns,
                                                                                        [col.id]: e.target.value
                                                                                    }
                                                                                })}
                                                                                style={{
                                                                                    flex: 1,
                                                                                    padding: '6px',
                                                                                    border: '1px solid #ddd',
                                                                                    borderRadius: '3px',
                                                                                    fontSize: '13px'
                                                                                }}
                                                                            />
                                                                            <input
                                                                                type="time"
                                                                                value={editFormData.times?.[col.id] || '09:00'}
                                                                                onChange={(e) => setEditFormData({
                                                                                    ...editFormData,
                                                                                    times: {
                                                                                        ...editFormData.times,
                                                                                        [col.id]: e.target.value
                                                                                    }
                                                                                })}
                                                                                style={{
                                                                                    width: '100px',
                                                                                    padding: '6px',
                                                                                    border: '1px solid #ddd',
                                                                                    borderRadius: '3px',
                                                                                    fontSize: '13px'
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    ) : (
                                                                        <input
                                                                            type="text"
                                                                            value={editFormData.columns?.[col.id] || ''}
                                                                            onChange={(e) => setEditFormData({
                                                                                ...editFormData,
                                                                                columns: {
                                                                                    ...editFormData.columns,
                                                                                    [col.id]: e.target.value
                                                                                }
                                                                            })}
                                                                            style={{
                                                                                width: '100%',
                                                                                padding: '6px',
                                                                                border: '1px solid #ddd',
                                                                                borderRadius: '3px',
                                                                                fontSize: '13px'
                                                                            }}
                                                                        />
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                </div>
                                            ) : (
                                                <details>
                                                    <summary style={{ 
                                                        cursor: 'pointer', 
                                                        color: '#0073ea',
                                                        fontWeight: '500',
                                                        userSelect: 'none'
                                                    }}>
                                                        View {item.column_values.length} Columns
                                                    </summary>
                                                    <div style={{ 
                                                        marginTop: '12px', 
                                                        padding: '12px', 
                                                        backgroundColor: '#f0f4f8', 
                                                        borderRadius: '4px',
                                                        border: '1px solid #d0dae5'
                                                    }}>
                                                        {item.column_values.map((col, idx) => {
                                                            const columnInfo = boardColumns.find(c => c.id === col.id);
                                                            return (
                                                                <div key={idx} style={{ 
                                                                    marginBottom: '8px', 
                                                                    fontSize: '13px',
                                                                    padding: '6px',
                                                                    backgroundColor: 'white',
                                                                    borderRadius: '3px'
                                                                }}>
                                                                    <strong style={{ color: '#333' }}>
                                                                        {columnInfo?.title || col.id}
                                                                    </strong>
                                                                    <span style={{ color: '#999', fontSize: '11px', marginLeft: '8px' }}>
                                                                        ({col.type})
                                                                    </span>
                                                                    <div style={{ color: '#555', marginTop: '4px' }}>
                                                                        {formatDateValue(col) || <em style={{ color: '#999' }}>empty</em>}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </details>
                                            )}
                                        </td>
                                        <td style={{ padding: '14px' }}>
                                            {editingItemId === item.id ? (
                                                <div style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
                                                    <button
                                                        onClick={saveChanges}
                                                        disabled={loading}
                                                        style={{
                                                            padding: '8px 16px',
                                                            backgroundColor: '#28a745',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: loading ? 'not-allowed' : 'pointer',
                                                            fontSize: '13px',
                                                            fontWeight: 'bold'
                                                        }}
                                                    >
                                                        {loading ? 'Saving...' : '💾 Save'}
                                                    </button>
                                                    <button
                                                        onClick={cancelEditing}
                                                        disabled={loading}
                                                        style={{
                                                            padding: '8px 16px',
                                                            backgroundColor: '#6c757d',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: loading ? 'not-allowed' : 'pointer',
                                                            fontSize: '13px'
                                                        }}
                                                    >
                                                        ✕ Cancel
                                                    </button>
                                                </div>
                                            ) : (
                                                <div style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
                                                    <button
                                                        onClick={() => startEditing(item)}
                                                        disabled={loading || editingItemId !== null}
                                                        style={{
                                                            padding: '8px 16px',
                                                            backgroundColor: editingItemId !== null ? '#ccc' : '#0073ea',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: (loading || editingItemId !== null) ? 'not-allowed' : 'pointer',
                                                            fontSize: '13px',
                                                            fontWeight: 'bold'
                                                        }}
                                                    >
                                                        ✏️ Edit
                                                    </button>
                                                    <button
                                                        onClick={() => deleteItem(item.id, item.name)}
                                                        disabled={loading || editingItemId !== null}
                                                        style={{
                                                            padding: '8px 16px',
                                                            backgroundColor: (loading || editingItemId !== null) ? '#ccc' : '#dc3545',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: (loading || editingItemId !== null) ? 'not-allowed' : 'pointer',
                                                            fontSize: '13px',
                                                            fontWeight: 'bold'
                                                        }}
                                                    >
                                                        🗑️ Delete
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Instructions */}
            {!showBoardSelection && approvedBoards.length === 0 && boards.length === 0 && !loading && !error && (
                <div style={{ 
                    padding: '30px', 
                    backgroundColor: '#e6f2ff', 
                    borderRadius: '8px',
                    marginTop: '20px',
                    border: '1px solid #b3d9ff'
                }}>
                    <h3 style={{ marginTop: 0, color: '#0073ea' }}>🚀 New Workflow - How to use:</h3>
                    <ol style={{ lineHeight: '1.8', color: '#333' }}>
                        <li><strong>Connect:</strong> Enter your Monday.com API token above</li>
                        <li><strong>Fetch:</strong> Click "Fetch Boards" to load all available boards</li>
                        <li><strong>Select:</strong> Choose which boards you want to work with (multi-select)</li>
                        <li><strong>Approve:</strong> Confirm your selection to load board structures</li>
                        <li><strong>Create:</strong> Add new items to any selected board with all its fields</li>
                    </ol>
                    
                    <div style={{ 
                        marginTop: '20px', 
                        padding: '15px', 
                        backgroundColor: '#d4edda', 
                        borderRadius: '4px',
                        border: '1px solid #c3e6cb'
                    }}>
                        <strong style={{ color: '#155724' }}>✨ What's New:</strong>
                        <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#155724' }}>
                            • <strong>Board Selection:</strong> Choose only the boards you need<br/>
                            • <strong>Dynamic Forms:</strong> Each board shows its own fields (text, date, email, etc.)<br/>
                            • <strong>Smart Creation:</strong> Calendar boards → meetings, CRM boards → leads, etc.<br/>
                            • <strong>Field Types:</strong> Supports text, date, email, phone, long-text fields
                        </p>
                    </div>

                    <div style={{ 
                        marginTop: '15px', 
                        padding: '15px', 
                        backgroundColor: '#fff3cd', 
                        borderRadius: '4px',
                        border: '1px solid #ffc107'
                    }}>
                        <strong style={{ color: '#856404' }}>⚠️ Security Note:</strong>
                        <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#856404' }}>
                            For testing, the token is stored in your browser's localStorage. 
                            For production use, always store API tokens securely on the backend, never in frontend code.
                        </p>
                    </div>
                </div>
            )}

            {/* Success message when data is loaded */}
            {boards.length > 0 && items.length === 0 && !loading && (
                <div style={{
                    padding: '20px',
                    backgroundColor: '#d4edda',
                    borderRadius: '8px',
                    border: '1px solid #c3e6cb',
                    color: '#155724'
                }}>
                    <strong>✓ Connected Successfully!</strong>
                    <p style={{ margin: '8px 0 0 0' }}>
                        Select a board above to view and edit its calendar items.
                    </p>
                </div>
            )}

            {/* Help note when items are displayed */}
            {items.length > 0 && !editingItemId && (
                <div style={{
                    padding: '15px',
                    backgroundColor: '#e7f3ff',
                    borderRadius: '8px',
                    border: '1px solid #b3d9ff',
                    marginTop: '20px',
                    color: '#004085'
                }}>
                    <strong>💡 Tips:</strong> 
                    <br/>• Click "✏️ Edit" to modify meeting details
                    <br/>• Click "🗑️ Delete" to remove meetings
                    <br/>• Click "📅 Create Meeting" to schedule new meetings
                    <br/>• In the scheduler: Green slots = Available, Red slots = Busy (click to view/delete)
                </div>
            )}

            {/* Create Meeting Modal */}
            {/* Board Selection Modal */}
            {showBoardSelection && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    overflowY: 'auto',
                    padding: '20px'
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '30px',
                        borderRadius: '12px',
                        maxWidth: '900px',
                        width: '100%',
                        maxHeight: '90vh',
                        overflowY: 'auto'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h2 style={{ margin: 0, color: '#333' }}>📋 Select Boards to Work With</h2>
                            <div style={{ fontSize: '14px', color: '#666' }}>
                                {selectedBoards.length} selected
                            </div>
                        </div>

                        <p style={{ marginBottom: '20px', color: '#666' }}>
                            Choose which boards you want to work with. You'll be able to create new items for each selected board.
                        </p>

                        <div style={{ 
                            maxHeight: '400px',
                            overflowY: 'auto',
                            border: '1px solid #eee',
                            borderRadius: '8px',
                            padding: '10px',
                            marginBottom: '20px'
                        }}>
                            {boards.map((board) => (
                                <div
                                    key={board.id}
                                    onClick={() => toggleBoardSelection(board.id)}
                                    style={{
                                        padding: '12px',
                                        border: selectedBoards.includes(board.id) ? '2px solid #28a745' : '1px solid #ddd',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        backgroundColor: selectedBoards.includes(board.id) ? '#f8fff8' : 'white',
                                        marginBottom: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedBoards.includes(board.id)}
                                        onChange={() => {}} // Handled by div click
                                        style={{ 
                                            transform: 'scale(1.2)',
                                            accentColor: '#28a745'
                                        }}
                                    />
                                    <div style={{ flex: 1 }}>
                                        <h4 style={{ margin: '0 0 4px 0', color: '#333' }}>{board.name}</h4>
                                        <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                                            Type: <strong>{board.board_kind}</strong> | ID: {board.id}
                                        </p>
                                        {board.description && (
                                            <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#888' }}>
                                                {board.description.length > 100 ? 
                                                    board.description.substring(0, 100) + '...' : 
                                                    board.description
                                                }
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                onClick={approveBoardSelection}
                                disabled={loading || selectedBoards.length === 0}
                                style={{
                                    flex: 1,
                                    padding: '14px',
                                    backgroundColor: selectedBoards.length > 0 ? '#28a745' : '#ccc',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: (loading || selectedBoards.length === 0) ? 'not-allowed' : 'pointer',
                                    fontSize: '16px',
                                    fontWeight: 'bold'
                                }}
                            >
                                {loading ? 'Loading...' : `✓ Confirm Selection (${selectedBoards.length} boards)`}
                            </button>
                            <button
                                onClick={() => {
                                    setShowBoardSelection(false);
                                    setSelectedBoards([]);
                                }}
                                disabled={loading}
                                style={{
                                    padding: '14px 24px',
                                    backgroundColor: '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    fontSize: '16px'
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Create Item Modal */}
            {showCreateItemModal && currentBoard && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    overflowY: 'auto',
                    padding: '20px'
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '30px',
                        borderRadius: '12px',
                        maxWidth: '600px',
                        width: '100%',
                        maxHeight: '90vh',
                        overflowY: 'auto'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h2 style={{ margin: 0, color: '#0073ea' }}>
                                ➕ Add New {currentBoard.board_kind === 'public' ? 'Item' : currentBoard.board_kind}
                            </h2>
                            <button
                                onClick={() => {
                                    setShowCreateItemModal(false);
                                    setCurrentBoard(null);
                                    setNewItemData({});
                                }}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                    color: '#999'
                                }}
                            >
                                ✕
                            </button>
                        </div>

                        <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f0f8ff', borderRadius: '6px' }}>
                            <h4 style={{ margin: '0 0 8px 0', color: '#0073ea' }}>{currentBoard.name}</h4>
                            <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>
                                Board Type: <strong>{currentBoard.board_kind}</strong>
                            </p>
                        </div>

                        {/* Item Name */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>
                                Item Name *
                            </label>
                            <input
                                type="text"
                                value={newItemData.name || ''}
                                onChange={(e) => setNewItemData({ ...newItemData, name: e.target.value })}
                                placeholder={`Enter ${currentBoard.board_kind === 'public' ? 'item' : currentBoard.board_kind} name`}
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    fontSize: '16px',
                                    borderRadius: '6px',
                                    border: '2px solid #ddd'
                                }}
                            />
                        </div>

                        {/* Dynamic Fields - ALL COLUMN TYPES */}
                        <div style={{ marginBottom: '20px' }}>
                            <h4 style={{ marginBottom: '15px', color: '#333' }}>
                                All Available Fields ({(boardColumns[currentBoard.id] || []).length} total)
                            </h4>
                            
                            {/* Debug info */}
                            {console.log('🎨 Rendering form for board:', currentBoard.id, 'Columns:', boardColumns[currentBoard.id])}
                            
                            <div style={{ display: 'grid', gap: '15px' }}>
                                {(boardColumns[currentBoard.id] || [])
                                    .filter(col => col.id !== 'name') // Skip name column as it's handled separately
                                    .map((column) => (
                                        <div key={column.id}>
                                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#555' }}>
                                                {column.title}
                                                <span style={{ color: '#999', fontSize: '12px', marginLeft: '6px' }}>
                                                    ({column.type})
                                                </span>
                                            </label>
                                            
                                            {/* Handle different column types */}
                                            {column.type === 'long-text' ? (
                                                <textarea
                                                    value={newItemData[column.id] || ''}
                                                    onChange={(e) => setNewItemData({ ...newItemData, [column.id]: e.target.value })}
                                                    placeholder={`Enter ${column.title.toLowerCase()}`}
                                                    rows={3}
                                                    style={{
                                                        width: '100%',
                                                        padding: '10px',
                                                        fontSize: '14px',
                                                        borderRadius: '4px',
                                                        border: '1px solid #ddd',
                                                        resize: 'vertical'
                                                    }}
                                                />
                                            ) : column.type === 'status' ? (
                                                <select
                                                    value={newItemData[column.id] || ''}
                                                    onChange={(e) => setNewItemData({ ...newItemData, [column.id]: e.target.value })}
                                                    style={{
                                                        width: '100%',
                                                        padding: '10px',
                                                        fontSize: '14px',
                                                        borderRadius: '4px',
                                                        border: '1px solid #ddd'
                                                    }}
                                                >
                                                    <option value="">Select status...</option>
                                                    {(column.labels && Array.isArray(column.labels)) ? (
                                                        column.labels.map(label => (
                                                            <option key={label.id || label.name} value={label.name}>
                                                                {label.name}
                                                            </option>
                                                        ))
                                                    ) : (
                                                        <option value="" disabled>Loading options...</option>
                                                    )}
                                                </select>
                                            ) : column.type === 'dropdown' ? (
                                                <select
                                                    value={newItemData[column.id] || ''}
                                                    onChange={(e) => setNewItemData({ ...newItemData, [column.id]: e.target.value })}
                                                    style={{
                                                        width: '100%',
                                                        padding: '10px',
                                                        fontSize: '14px',
                                                        borderRadius: '4px',
                                                        border: '1px solid #ddd'
                                                    }}
                                                >
                                                    <option value="">Select option...</option>
                                                    {(column.labels && Array.isArray(column.labels)) ? (
                                                        column.labels.map(label => (
                                                            <option key={label.id || label.name} value={label.name}>
                                                                {label.name}
                                                            </option>
                                                        ))
                                                    ) : (
                                                        <option value="" disabled>Loading options...</option>
                                                    )}
                                                </select>
                                            ) : column.type === 'numbers' ? (
                                                <input
                                                    type="number"
                                                    value={newItemData[column.id] || ''}
                                                    onChange={(e) => setNewItemData({ ...newItemData, [column.id]: e.target.value })}
                                                    placeholder={`Enter ${column.title.toLowerCase()}`}
                                                    style={{
                                                        width: '100%',
                                                        padding: '10px',
                                                        fontSize: '14px',
                                                        borderRadius: '4px',
                                                        border: '1px solid #ddd'
                                                    }}
                                                />
                                            ) : column.type === 'people' ? (
                                                <div style={{
                                                    padding: '10px',
                                                    backgroundColor: '#f8f9fa',
                                                    borderRadius: '4px',
                                                    border: '1px solid #ddd',
                                                    color: '#666',
                                                    fontStyle: 'italic'
                                                }}>
                                                    People field - assign users after item creation
                                                </div>
                                            ) : column.type === 'timeline' ? (
                                                <div style={{
                                                    padding: '10px',
                                                    backgroundColor: '#f8f9fa',
                                                    borderRadius: '4px',
                                                    border: '1px solid #ddd',
                                                    color: '#666',
                                                    fontStyle: 'italic'
                                                }}>
                                                    Timeline field - set dates after item creation
                                                </div>
                                            ) : (
                                                <input
                                                    type={column.type === 'date' ? 'date' : 
                                                          column.type === 'email' ? 'email' : 
                                                          column.type === 'phone' ? 'tel' : 
                                                          column.type === 'url' ? 'url' :
                                                          'text'}
                                                    value={newItemData[column.id] || ''}
                                                    onChange={(e) => setNewItemData({ ...newItemData, [column.id]: e.target.value })}
                                                    placeholder={`Enter ${column.title.toLowerCase()}`}
                                                    style={{
                                                        width: '100%',
                                                        padding: '10px',
                                                        fontSize: '14px',
                                                        borderRadius: '4px',
                                                        border: '1px solid #ddd'
                                                    }}
                                                />
                                            )}
                                        </div>
                                    ))}
                            </div>
                            
                            <div style={{
                                marginTop: '15px',
                                padding: '12px',
                                backgroundColor: '#e7f3ff',
                                borderRadius: '4px',
                                fontSize: '13px',
                                color: '#004085'
                            }}>
                                <strong>💡 Field Types Supported:</strong>
                                <br/>✅ Text, Date, Email, Phone, URL, Numbers, Long-text
                                <br/>✅ Status, Dropdown (with options)
                                <br/>⚠️ People, Timeline (create item first, then assign)
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                onClick={createBoardItem}
                                disabled={loading || !newItemData.name}
                                style={{
                                    flex: 1,
                                    padding: '14px',
                                    backgroundColor: newItemData.name ? '#28a745' : '#ccc',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: (loading || !newItemData.name) ? 'not-allowed' : 'pointer',
                                    fontSize: '16px',
                                    fontWeight: 'bold'
                                }}
                            >
                                {loading ? 'Creating...' : '✓ Create Item'}
                            </button>
                            <button
                                onClick={() => {
                                    setShowCreateItemModal(false);
                                    setCurrentBoard(null);
                                    setNewItemData({});
                                }}
                                disabled={loading}
                                style={{
                                    padding: '14px 24px',
                                    backgroundColor: '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    fontSize: '16px'
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Board Data View Modal */}
            {showBoardDataModal && currentBoard && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    overflowY: 'auto',
                    padding: '20px'
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '30px',
                        borderRadius: '12px',
                        maxWidth: '95vw',
                        width: '100%',
                        maxHeight: '90vh',
                        overflowY: 'auto'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h2 style={{ margin: 0, color: '#0073ea' }}>
                                👁️ {currentBoard.name} - Data View
                            </h2>
                            <button
                                onClick={() => {
                                    setShowBoardDataModal(false);
                                    setCurrentBoard(null);
                                }}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                    color: '#999'
                                }}
                            >
                                ✕
                            </button>
                        </div>

                        <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f0f8ff', borderRadius: '6px' }}>
                            <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                                <div>
                                    <h4 style={{ margin: '0 0 4px 0', color: '#0073ea' }}>{currentBoard.name}</h4>
                                    <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>
                                        Type: <strong>{currentBoard.board_kind}</strong> | 
                                        {(boardColumns[currentBoard.id] || []).length} columns | 
                                        {(boardItems[currentBoard.id] || []).length} items
                                    </p>
                                </div>
                                <button
                                    onClick={() => {
                                        setShowBoardDataModal(false);
                                        openCreateItemModal(currentBoard);
                                    }}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: '#28a745',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    ➕ Add New Item
                                </button>
                            </div>
                        </div>

                        {/* Column Information */}
                        <div style={{ marginBottom: '20px' }}>
                            <h4 style={{ color: '#333', marginBottom: '10px' }}>📋 Board Structure</h4>
                            <div style={{ 
                                display: 'flex', 
                                flexWrap: 'wrap', 
                                gap: '8px',
                                padding: '15px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '6px',
                                border: '1px solid #ddd'
                            }}>
                                {(boardColumns[currentBoard.id] || []).map(col => (
                                    <span 
                                        key={col.id}
                                        style={{ 
                                            padding: '4px 10px', 
                                            backgroundColor: col.type === 'date' ? '#e3f2fd' : 
                                                            col.type === 'status' ? '#f3e5f5' :
                                                            col.type === 'people' ? '#e8f5e8' :
                                                            col.type === 'numbers' ? '#fff3e0' :
                                                            '#f5f5f5',
                                            border: `1px solid ${col.type === 'date' ? '#2196f3' :
                                                            col.type === 'status' ? '#9c27b0' :
                                                            col.type === 'people' ? '#4caf50' :
                                                            col.type === 'numbers' ? '#ff9800' :
                                                            '#999'}30`,
                                            borderRadius: '12px', 
                                            fontSize: '12px',
                                            color: '#333',
                                            fontWeight: '500'
                                        }}
                                    >
                                        {col.title} ({col.type})
                                    </span>
                                ))}
                            </div>
                        </div>

                        {/* Search Section */}
                        <div style={{ marginBottom: '20px' }}>
                            <h4 style={{ color: '#333', marginBottom: '12px' }}>🔍 Search & Filter</h4>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <input
                                    type="text"
                                    placeholder="Search by name, email, phone, etc..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    style={{
                                        flex: 2,
                                        padding: '12px',
                                        fontSize: '14px',
                                        borderRadius: '6px',
                                        border: '2px solid #ddd',
                                        outline: 'none'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#0073ea'}
                                    onBlur={(e) => e.target.style.borderColor = '#ddd'}
                                />
                                <select
                                    value={searchColumn}
                                    onChange={(e) => setSearchColumn(e.target.value)}
                                    style={{
                                        flex: 1,
                                        padding: '12px',
                                        fontSize: '14px',
                                        borderRadius: '6px',
                                        border: '2px solid #ddd'
                                    }}
                                >
                                    <option value="all">All Columns</option>
                                    <option value="name">Item Name Only</option>
                                    {(boardColumns[currentBoard.id] || [])
                                        .filter(col => col.id !== 'name' && (col.type === 'text' || col.type === 'email' || col.type === 'phone' || col.type === 'status'))
                                        .map(col => (
                                            <option key={col.id} value={col.id}>
                                                {col.title}
                                            </option>
                                        ))}
                                </select>
                                {searchTerm && (
                                    <button
                                        onClick={() => setSearchTerm('')}
                                        style={{
                                            padding: '12px 20px',
                                            backgroundColor: '#dc3545',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                            {searchTerm && (
                                <div style={{ 
                                    marginTop: '10px', 
                                    padding: '10px', 
                                    backgroundColor: '#e7f3ff',
                                    borderRadius: '4px',
                                    fontSize: '13px',
                                    color: '#004085'
                                }}>
                                    🔍 Found <strong>{getFilteredItems().length}</strong> results 
                                    {searchColumn !== 'all' && ` in "${boardColumns[currentBoard.id]?.find(c => c.id === searchColumn)?.title || 'selected column'}"`}
                                </div>
                            )}
                        </div>

                        {/* Existing Items Table */}
                        <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                <h4 style={{ color: '#333', margin: 0 }}>
                                    📊 {searchTerm ? 'Search Results' : 'All Items'} ({getFilteredItems().length} {searchTerm && `of ${(boardItems[currentBoard.id] || []).length}`} items)
                                </h4>
                                {(boardItems[currentBoard.id] || []).length >= 2500 && (
                                    <div style={{
                                        padding: '6px 12px',
                                        backgroundColor: '#fff3cd',
                                        border: '1px solid #ffeaa7',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        color: '#856404'
                                    }}>
                                        ⚠️ Showing first 2500 items (board may have more)
                                    </div>
                                )}
                                {(boardItems[currentBoard.id] || []).length > 0 && (boardItems[currentBoard.id] || []).length < 2500 && (
                                    <div style={{
                                        padding: '6px 12px',
                                        backgroundColor: '#d4edda',
                                        border: '1px solid #c3e6cb',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        color: '#155724'
                                    }}>
                                        ✅ All {(boardItems[currentBoard.id] || []).length} items loaded
                                    </div>
                                )}
                            </div>
                            
                            {getFilteredItems().length === 0 ? (
                                <div style={{
                                    padding: '40px',
                                    textAlign: 'center',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '8px',
                                    color: '#666'
                                }}>
                                    {searchTerm ? (
                                        <>
                                            <h3 style={{ color: '#999', marginBottom: '10px' }}>🔍 No Results Found</h3>
                                            <p>No items match your search "{searchTerm}"</p>
                                            <button
                                                onClick={() => setSearchTerm('')}
                                                style={{
                                                    marginTop: '10px',
                                                    padding: '10px 20px',
                                                    backgroundColor: '#0073ea',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                Clear Search
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <h3 style={{ color: '#999', marginBottom: '10px' }}>📭 No Items Yet</h3>
                                            <p>This board is empty. Click "Add New Item" to create the first entry.</p>
                                        </>
                                    )}
                                </div>
                            ) : (
                                <div style={{ 
                                    overflowX: 'auto',
                                    maxHeight: '500px',
                                    overflowY: 'auto',
                                    border: '1px solid #ddd',
                                    borderRadius: '6px',
                                    position: 'relative'
                                }}>
                                    <table style={{ 
                                        width: '100%', 
                                        borderCollapse: 'collapse',
                                        backgroundColor: 'white'
                                    }}>
                                        <thead style={{ position: 'sticky', top: 0, backgroundColor: '#0073ea', color: 'white' }}>
                                            <tr>
                                                <th style={{ 
                                                    padding: '12px', 
                                                    textAlign: 'left', 
                                                    fontWeight: 'bold', 
                                                    minWidth: '150px'
                                                }}>
                                                    Item Name
                                                </th>
                                                {(boardColumns[currentBoard.id] || [])
                                                    .filter(col => col.id !== 'name')
                                                    .map(col => (
                                                        <th key={col.id} style={{ 
                                                            padding: '12px', 
                                                            textAlign: 'left', 
                                                            fontWeight: 'bold',
                                                            minWidth: '140px',
                                                            fontSize: '13px',
                                                            whiteSpace: 'nowrap'
                                                        }}>
                                                            {col.title}
                                                            <br/>
                                                            <span style={{ fontSize: '10px', opacity: 0.8 }}>({col.type})</span>
                                                        </th>
                                                    ))}
                                                <th style={{ 
                                                    padding: '12px', 
                                                    textAlign: 'left', 
                                                    fontWeight: 'bold', 
                                                    minWidth: '120px'
                                                }}>
                                                    Created
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {getFilteredItems().map((item, idx) => (
                                                <tr 
                                                    key={item.id} 
                                                    style={{ 
                                                        borderBottom: '1px solid #eee',
                                                        backgroundColor: idx % 2 === 0 ? 'white' : '#f9f9f9'
                                                    }}
                                                >
                                                    <td style={{ 
                                                        padding: '12px', 
                                                        fontWeight: '500',
                                                        minWidth: '150px',
                                                        backgroundColor: searchTerm && item.name.toLowerCase().includes(searchTerm.toLowerCase()) ? 
                                                            '#fff59d' : 
                                                            (idx % 2 === 0 ? 'white' : '#f9f9f9')
                                                    }}>
                                                        {item.name}
                                                    </td>
                                                    {(boardColumns[currentBoard.id] || [])
                                                        .filter(col => col.id !== 'name')
                                                        .map(col => {
                                                            const columnValue = item.column_values.find(cv => cv.id === col.id);
                                                            let displayValue = columnValue?.text || '';
                                                            
                                                            // Better value formatting based on type
                                                            if (col.type === 'date' && columnValue?.value) {
                                                                try {
                                                                    const dateData = JSON.parse(columnValue.value);
                                                                    if (dateData.date && dateData.time) {
                                                                        displayValue = `${dateData.date} ${dateData.time.substring(0, 5)}`;
                                                                    } else {
                                                                        displayValue = dateData.date || columnValue.text;
                                                                    }
                                                                } catch { }
                                                            } else if (col.type === 'status' && columnValue?.value) {
                                                                try {
                                                                    const statusData = JSON.parse(columnValue.value);
                                                                    displayValue = statusData.text || columnValue.text;
                                                                } catch { }
                                                            } else if (col.type === 'people' && columnValue?.value) {
                                                                try {
                                                                    const peopleData = JSON.parse(columnValue.value);
                                                                    if (peopleData.personsAndTeams) {
                                                                        displayValue = peopleData.personsAndTeams.map(p => p.name).join(', ');
                                                                    }
                                                                } catch { }
                                                            }
                                                            
                                                            // Check if this cell matches the search
                                                            const isMatch = searchTerm && displayValue && 
                                                                displayValue.toLowerCase().includes(searchTerm.toLowerCase());
                                                            
                                                            return (
                                                                <td key={col.id} style={{ 
                                                                    padding: '12px', 
                                                                    fontSize: '13px',
                                                                    minWidth: '140px',
                                                                    maxWidth: '200px',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                    backgroundColor: isMatch ? '#fff59d' : 'inherit'
                                                                }}>
                                                                    {displayValue || <em style={{ color: '#ccc' }}>empty</em>}
                                                                </td>
                                                            );
                                                        })}
                                                    <td style={{ 
                                                        padding: '12px', 
                                                        fontSize: '12px', 
                                                        color: '#666',
                                                        minWidth: '120px'
                                                    }}>
                                                        {new Date(item.created_at).toLocaleDateString()}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Old Meeting Modal */}
            {showMeetingModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    overflowY: 'auto',
                    padding: '20px'
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '30px',
                        borderRadius: '12px',
                        maxWidth: '800px',
                        width: '100%',
                        maxHeight: '90vh',
                        overflowY: 'auto'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h2 style={{ margin: 0, color: '#333' }}>📅 Create New Meeting</h2>
                            <button
                                onClick={() => {
                                    setShowMeetingModal(false);
                                    setNewMeeting({ name: '', date: '', time: '' });
                                    setSelectedTimeSlot(null);
                                }}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                    color: '#999'
                                }}
                            >
                                ✕
                            </button>
                        </div>

                        {/* Date Selector */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#555' }}>
                                Select Date:
                            </label>
                            <input
                                type="date"
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                style={{
                                    padding: '12px',
                                    fontSize: '16px',
                                    borderRadius: '6px',
                                    border: '2px solid #ddd',
                                    width: '100%'
                                }}
                            />
                        </div>

                        {/* Time Slots */}
                        <div style={{ marginBottom: '20px' }}>
                            <h3 style={{ color: '#333', marginBottom: '15px' }}>Time Slots for {selectedDate}</h3>
                            <p style={{ color: '#666', fontSize: '14px', marginBottom: '15px' }}>
                                🟢 <strong>Green = Available</strong> (Click to schedule) | 🔴 <strong>Red = Busy</strong> (Click to view/delete)
                            </p>
                            <div style={{ display: 'grid', gap: '10px' }}>
                                {timeSlots.map((timeSlot) => {
                                    const isOccupied = isTimeSlotOccupied(timeSlot);
                                    const isSelected = selectedTimeSlot === timeSlot;
                                    const meetings = getMeetingsForDate();
                                    const meeting = meetings.find(m => {
                                        const dateCol = m.column_values.find(c => c.type === 'date' && c.value);
                                        if (dateCol && dateCol.value) {
                                            try {
                                                const data = JSON.parse(dateCol.value);
                                                return data.time && data.time.substring(0, 5) === timeSlot;
                                            } catch { return false; }
                                        }
                                        return false;
                                    });

                                    return (
                                        <div
                                            key={timeSlot}
                                            onClick={() => handleTimeSlotClick(timeSlot)}
                                            style={{
                                                padding: '15px',
                                                border: isSelected ? '3px solid #28a745' : (isOccupied ? '2px solid #dc3545' : '2px solid #ddd'),
                                                borderRadius: '8px',
                                                cursor: 'pointer',
                                                backgroundColor: isSelected ? '#d4edda' : (isOccupied ? '#f8d7da' : '#f8f9fa'),
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                transition: 'all 0.2s',
                                                opacity: 1
                                            }}
                                        >
                                            <div>
                                                <strong style={{ fontSize: '18px', color: '#333' }}>{timeSlot}</strong>
                                                {isOccupied && meeting && (
                                                    <div style={{ marginTop: '4px', color: '#666', fontSize: '14px' }}>
                                                        📅 {meeting.name}
                                                    </div>
                                                )}
                                                {!isOccupied && !isSelected && (
                                                    <div style={{ marginTop: '4px', color: '#28a745', fontSize: '13px' }}>
                                                        ✓ Click to schedule
                                                    </div>
                                                )}
                                                {isSelected && !isOccupied && (
                                                    <div style={{ marginTop: '4px', color: '#28a745', fontSize: '13px', fontWeight: 'bold' }}>
                                                        ✓ Selected for new meeting
                                                    </div>
                                                )}
                                                {isSelected && isOccupied && (
                                                    <div style={{ marginTop: '4px', color: '#dc3545', fontSize: '13px', fontWeight: 'bold' }}>
                                                        ⚠️ Selected for viewing/deletion
                                                    </div>
                                                )}
                                            </div>
                                            <div>
                                                {isOccupied ? (
                                                    <span style={{ 
                                                        backgroundColor: '#dc3545', 
                                                        color: 'white', 
                                                        padding: '6px 16px', 
                                                        borderRadius: '20px',
                                                        fontSize: '12px',
                                                        fontWeight: 'bold'
                                                    }}>
                                                        Busy
                                                    </span>
                                                ) : (
                                                    <span style={{ 
                                                        backgroundColor: '#28a745', 
                                                        color: 'white', 
                                                        padding: '6px 16px', 
                                                        borderRadius: '20px',
                                                        fontSize: '12px',
                                                        fontWeight: 'bold'
                                                    }}>
                                                        Free
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Meeting Details Form */}
                        {selectedTimeSlot && (
                                        <div style={{ 
                                                marginTop: '30px', 
                                                padding: '20px', 
                                                backgroundColor: newMeeting.id ? '#fff8e1' : '#f0f8ff', 
                                                borderRadius: '8px',
                                                border: newMeeting.id ? '2px solid #ff9800' : '2px solid #0073ea'
                                            }}>
                                                <h3 style={{ marginTop: 0, color: newMeeting.id ? '#ff9800' : '#0073ea' }}>
                                                    {newMeeting.id ? '📅 Existing Meeting' : '📅 New Meeting'}
                                                </h3>
                                
                                <div style={{ marginBottom: '15px' }}>
                                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#555' }}>
                                        Meeting Title *
                                    </label>
                                    <input
                                        type="text"
                                        value={newMeeting.name}
                                        onChange={(e) => setNewMeeting({ ...newMeeting, name: e.target.value })}
                                        placeholder={newMeeting.id ? "Existing meeting" : "Enter meeting title"}
                                        readOnly={!!newMeeting.id}
                                        style={{
                                            width: '100%',
                                            padding: '12px',
                                            fontSize: '16px',
                                            borderRadius: '6px',
                                            border: '2px solid #ddd',
                                            backgroundColor: newMeeting.id ? '#f5f5f5' : 'white'
                                        }}
                                    />
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#555' }}>
                                            Date
                                        </label>
                                        <input
                                            type="date"
                                            value={newMeeting.date}
                                            readOnly
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                fontSize: '16px',
                                                borderRadius: '6px',
                                                border: '2px solid #ddd',
                                                backgroundColor: '#f5f5f5'
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#555' }}>
                                            Time
                                        </label>
                                        <input
                                            type="time"
                                            value={newMeeting.time}
                                            readOnly
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                fontSize: '16px',
                                                borderRadius: '6px',
                                                border: '2px solid #ddd',
                                                backgroundColor: '#f5f5f5'
                                            }}
                                        />
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: '10px' }}>
                                    {newMeeting.id ? (
                                        // Existing meeting - show delete button
                                        <button
                                            onClick={() => deleteItem(newMeeting.id, newMeeting.name)}
                                            disabled={loading}
                                            style={{
                                                flex: 1,
                                                padding: '14px',
                                                backgroundColor: loading ? '#ccc' : '#dc3545',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '6px',
                                                cursor: loading ? 'not-allowed' : 'pointer',
                                                fontSize: '16px',
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            {loading ? 'Deleting...' : '🗑️ Delete Meeting'}
                                        </button>
                                    ) : (
                                        // New meeting - show create button
                                        <button
                                            onClick={createMeeting}
                                            disabled={loading || !newMeeting.name}
                                            style={{
                                                flex: 1,
                                                padding: '14px',
                                                backgroundColor: newMeeting.name ? '#28a745' : '#ccc',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '6px',
                                                cursor: (loading || !newMeeting.name) ? 'not-allowed' : 'pointer',
                                                fontSize: '16px',
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            {loading ? 'Creating...' : '✓ Create Meeting'}
                                        </button>
                                    )}
                                    <button
                                        onClick={() => {
                                            setSelectedTimeSlot(null);
                                            setNewMeeting({ name: '', date: '', time: '' });
                                        }}
                                        disabled={loading}
                                        style={{
                                            padding: '14px 24px',
                                            backgroundColor: '#6c757d',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: loading ? 'not-allowed' : 'pointer',
                                            fontSize: '16px'
                                        }}
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MondayCalendarTest;
